import {
  Box,
  Heading,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
  VStack,
  Stack,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Divider,
  useToast,
  Input,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  NumberInput,
  NumberInputField,
  Image,
  HStack,
  Checkbox,
  Badge,
  IconButton,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { getSubjects } from "API/subjectApi";
import { getPrograms } from "API/programApi";
import { fetchQuestionPapers, updateQuestionPaper } from "API/questionpaperAPI";
import { filterQuestion } from "API/questionApi";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";

/* ---------- Helpers ---------- */

const getBackendBaseUrl = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/:3000$/, ":9000").replace(/\/+$/, "");
  }
  return "";
};

// Build final image URL
const buildImageUrl = (img?: string): string | null => {
  if (!img || typeof img !== "string") return null;
  if (/^https?:\/\//i.test(img)) return img; // already absolute
  const base = getBackendBaseUrl();
  return `${base}/upload/${encodeURIComponent(img)}`;
};

// Render a small thumbnail (used in edit table)
const QuestionThumb = ({ question }: { question: any }) => {
  const src = buildImageUrl(question?.questionImg);
  if (!src) return null;
  return (
    <Image
      src={src}
      alt="question"
      boxSize="64px"
      objectFit="cover"
      borderRadius="md"
      borderWidth="1px"
    />
  );
};

// Render a larger preview (used in read-only preview)
const QuestionPreviewImage = ({ question }: { question: any }) => {
  const src = buildImageUrl(question?.questionImg);
  const bg = useColorModeValue("gray.50", "gray.900");
  if (!src) return null;
  return (
    <Image
      src={src}
      alt="question"
      boxSize="120px"
      objectFit="contain"
      borderRadius="md"
      borderWidth="1px"
      bg={bg}
    />
  );
};

// Compute section intended max (same logic as backend)
const computeSectionIntendedMax = (section: any): number => {
  const selectionType = String(section?.selectionType || "ALL").toUpperCase(); // ALL, ANY, AT_LEAST, AT_MOST
  const sectionMarksMode = String(section?.sectionMarksMode || "SUM").toUpperCase(); // SUM, CAP
  const optionalCount =
    section?.optionalCount != null ? Number(section.optionalCount) : undefined;
  const sectionMarksCap =
    sectionMarksMode === "CAP" ? Number(section?.sectionMarksCap || 0) : undefined;

  const marksList = (section?.questions || [])
    .map((q: any) => Number(q?.marks || 0))
    .filter((m: number) => Number.isFinite(m) && m > 0)
    .sort((a: number, b: number) => b - a);

  let intended = 0;

  switch (selectionType) {
    case "ALL":
      intended = marksList.reduce((a: any, b: any) => a + b, 0);
      break;
    case "ANY":
    case "AT_MOST": {
      const n = Math.max(0, Number(optionalCount || 0));
      intended = marksList.slice(0, n).reduce((a: any, b: any) => a + b, 0);
      break;
    }
    case "AT_LEAST":
      intended = marksList.reduce((a: any, b: any) => a + b, 0);
      break;
    default:
      intended = marksList.reduce((a: any, b: any) => a + b, 0);
      break;
  }

  if (sectionMarksMode === "CAP") {
    if (!sectionMarksCap || sectionMarksCap <= 0) return intended;
    intended = Math.min(intended, sectionMarksCap);
  }

  return intended;
};

// New section factory (align with create flow defaults)
const createNewSection = (index: number) => ({
  name: `Section ${index + 1}`,
  selectionType: "ALL", // ALL, ANY, AT_LEAST, AT_MOST
  optionalCount: undefined, // only for ANY/AT_LEAST/AT_MOST
  selectionPolicy: "FIRST_N", // FIRST_N | BEST_N
  sectionMarksMode: "SUM", // SUM | CAP
  sectionMarksCap: undefined, // only for CAP
  instructions: "",
  shuffleQuestions: false,
  typeFilter: "", // UI filter per section
  questions: [] as Array<any>, // {questionId, marks, requiresStudentUpload?, ...snapshot}
});

const AllQuestionPapers = () => {
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedPaper, setSelectedPaper] = useState<any>(null);
  const [editedPaper, setEditedPaper] = useState<any>(null);
  const [editMode, setEditMode] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const queryClient = useQueryClient();
  const bgCardLight = useColorModeValue("gray.50", "gray.700");

  // Load dropdown data
  const { data: subjects = [] } = useQuery("subjects", getSubjects);
  const { data: programs = [] } = useQuery("programs", getPrograms);

  // All papers
  const { data: papersData, isLoading, error } = useQuery(
    "allQuestionPapers",
    fetchQuestionPapers
  );

  const questionPapers = Array.isArray(papersData)
    ? papersData
    : papersData?.papers || [];

  const filteredPapers = selectedSubject
    ? questionPapers.filter((paper: any) => paper.subject === selectedSubject)
    : questionPapers;

  // Questions for selected subject in edit modal
  const subjectForQuery = editedPaper?.subject || "";
  const {
    data: questionsData,
    isLoading: questionsLoading,
    refetch: refetchQuestions,
  } = useQuery(
    ["allQuestions", subjectForQuery],
    () =>
      subjectForQuery
        ? filterQuestion({ size: 1000, subject: subjectForQuery })
        : Promise.resolve({ questions: [] }),
    {
      enabled: !!subjectForQuery && isOpen && editMode, // only when editing
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (isOpen && editMode && subjectForQuery) {
      refetchQuestions();
    }
  }, [isOpen, editMode, subjectForQuery, refetchQuestions]);

  const updateMutation = useMutation(updateQuestionPaper, {
    onSuccess: () => {
      toast({
        id: selectedPaper?._id,
        description: "Question paper updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setEditMode(false);
      queryClient.invalidateQueries("allQuestionPapers");
    },
    onError: (err: any) => {
      toast({
        title: "Update failed",
        description:
          err?.response?.data?.message || err?.message || "Something went wrong.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    },
  });

  const handleView = (paper: any) => {
    setSelectedPaper(paper);
    // Deep copy and ensure rule fields & typeFilter exist
    const cloned = {
      ...paper,
      sections: (paper.sections || []).map((sec: any, idx: number) => ({
        name: sec.name || `Section ${idx + 1}`,
        selectionType: sec.selectionType || "ALL",
        optionalCount:
          typeof sec.optionalCount === "number" ? sec.optionalCount : undefined,
        sectionMarksMode: sec.sectionMarksMode || "SUM",
        sectionMarksCap:
          typeof sec.sectionMarksCap === "number" ? sec.sectionMarksCap : undefined,
        selectionPolicy: sec.selectionPolicy || "FIRST_N",
        instructions: sec.instructions || "",
        shuffleQuestions: !!sec.shuffleQuestions,
        typeFilter: "", // fresh filter for UI
        questions: (sec.questions || []).map((q: any) => ({ ...q })), // keep existing marks & flags
      })),
    };
    setEditedPaper(cloned);
    setEditMode(false);
    onOpen();
  };

  const handleSubjectChangeInEdit = (subject: string) => {
    setEditedPaper((prev: any) => ({ ...prev, subject }));
    // Clear all selected questions when subject changes
    setEditedPaper((prev: any) => {
      const clearedSections = prev.sections.map((section: any) => ({
        ...section,
        questions: [],
      }));
      return { ...prev, sections: clearedSections };
    });
    if (isOpen) {
      setTimeout(() => refetchQuestions(), 0);
    }
  };

  // Compute guidance total of current editedPaper
  const guidanceTotal = useMemo(() => {
    if (!editedPaper?.sections) return 0;
    return editedPaper.sections.reduce(
      (acc: number, sec: any) => acc + computeSectionIntendedMax(sec),
      0
    );
  }, [editedPaper]);

  // Selected question IDs across sections (for deduplication)
  const selectedQuestionIds = useMemo(() => {
    const set = new Set<string>();
    if (editedPaper?.sections) {
      editedPaper.sections.forEach((section: any) =>
        section.questions.forEach((q: any) => set.add(String(q.questionId)))
      );
    }
    return set;
  }, [editedPaper]);

  const handleUpdate = () => {
    if (!editedPaper) return;

    if (Number(guidanceTotal) !== Number(editedPaper.totalMarks)) {
      toast({
        title: "Validation Failed",
        description:
          `Total Marks (${editedPaper.totalMarks}) must equal Max possible (guide) (${guidanceTotal}). ` +
          `Adjust section rules/marks or update Total Marks.`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    updateMutation.mutate({
      id: editedPaper._id,
      updatedData: editedPaper,
    });
  };

  const addNewSection = () => {
    if (!editedPaper) return;
    const nextIndex = editedPaper.sections?.length || 0;
    const newSection = createNewSection(nextIndex);
    setEditedPaper((prev: any) => ({
      ...prev,
      sections: [...(prev.sections || []), newSection],
    }));
  };

  const removeSection = (sIdx: number) => {
    if (!editedPaper) return;
    const sections = [...editedPaper.sections];
    if (sections.length <= 1) {
      toast({
        title: "Cannot remove last section",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    sections.splice(sIdx, 1);
    const relabeled = sections.map((sec, idx) => {
      if (/^Section \d+$/.test(sec.name || "")) {
        return { ...sec, name: `Section ${idx + 1}` };
      }
      return sec;
    });
    setEditedPaper({ ...editedPaper, sections: relabeled });
  };

  return (
    <Box p={4}>
      <Heading mb={5}>All Question Papers</Heading>

      <Stack direction={["column", "row"]} mb={6}>
        <Select
          placeholder="Filter by Subject"
          width={["100%", "300px"]}
          value={selectedSubject}
          onChange={(e) => setSelectedSubject(e.target.value)}
        >
          {subjects.map((sub: any) => (
            <option key={sub._id} value={sub.name}>
              {sub.name}
            </option>
          ))}
        </Select>
      </Stack>

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <Text color="red.500">Failed to load question papers</Text>
      ) : (
        <SimpleGrid columns={[1, 2, 3]} spacing={5}>
          {filteredPapers.map((paper: any) => (
            <Box
              key={paper._id}
              p={5}
              borderWidth="1px"
              borderRadius="lg"
              bg={bgCardLight}
              shadow="sm"
              transition="transform 0.2s"
              _hover={{ transform: "scale(1.03)", shadow: "md" }}
              cursor="pointer"
              onClick={() => handleView(paper)}
            >
              <VStack align="start" spacing={2}>
                <Heading size="md" noOfLines={1}>
                  {paper.title}
                </Heading>
                <Text fontSize="sm" noOfLines={1}>
                  Subject: {paper.subject}
                </Text>
                <Text fontSize="sm">Duration: {paper.duration} mins</Text>
                <Text fontSize="sm">Total Marks: {paper.totalMarks}</Text>
                <Text fontSize="sm" noOfLines={1}>
                  Total Questions:{" "}
                  {paper.sections.reduce(
                    (acc: number, sec: any) => acc + sec.questions.length,
                    0
                  )}
                </Text>
                <Button size="sm" colorScheme="blue" mt={2}>
                  View / Edit
                </Button>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {editedPaper && isOpen && (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {editMode ? "Edit Question Paper" : editedPaper.title}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {editMode ? (
                <Stack spacing={4}>
                  {/* Top-level fields */}
                  <FormLabel>Title</FormLabel>
                  <Input
                    value={editedPaper.title}
                    onChange={(e) =>
                      setEditedPaper({ ...editedPaper, title: e.target.value })
                    }
                  />

                  <FormLabel>Subject</FormLabel>
                  <Select
                    value={editedPaper.subject}
                    onChange={(e) => handleSubjectChangeInEdit(e.target.value)}
                  >
                    <option value="">Select Subject</option>
                    {subjects.map((sub: any) => (
                      <option key={sub._id} value={sub.name}>
                        {sub.name}
                      </option>
                    ))}
                  </Select>

                  <FormLabel>Program</FormLabel>
                  <Select
                    value={editedPaper.programId || ""}
                    onChange={(e) =>
                      setEditedPaper({
                        ...editedPaper,
                        programId: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Program</option>
                    {programs.map((prog: any) => (
                      <option key={prog._id} value={prog._id}>
                        {prog.name}
                      </option>
                    ))}
                  </Select>

                  <FormLabel>Duration (minutes)</FormLabel>
                  <Input
                    type="number"
                    value={editedPaper.duration}
                    onChange={(e) =>
                      setEditedPaper({
                        ...editedPaper,
                        duration: Number(e.target.value),
                      })
                    }
                  />

                  <FormLabel>Total Marks</FormLabel>
                  <Input
                    type="number"
                    value={editedPaper.totalMarks}
                    onChange={(e) =>
                      setEditedPaper({
                        ...editedPaper,
                        totalMarks: Number(e.target.value),
                      })
                    }
                  />
                  <Text fontSize="sm" color="gray.600">
                    Max possible (guide) based on current sections and marks:{" "}
                    <b>{guidanceTotal}</b>
                  </Text>

                  {/* Add Section button */}
                  <Flex justify="flex-end" mt={2}>
                    <Button
                      leftIcon={<AddIcon />}
                      colorScheme="green"
                      size="sm"
                      onClick={addNewSection}
                    >
                      Add Section
                    </Button>
                  </Flex>

                  {/* Sections */}
                  {editedPaper.sections.map((section: any, sIdx: number) => {
                    // Questions selected in other sections for disabling duplicates
                    const otherSelectedIds = new Set<string>();
                    editedPaper.sections.forEach((sec: any, idx: number) => {
                      if (idx !== sIdx) {
                        sec.questions.forEach((q: any) =>
                          otherSelectedIds.add(q.questionId)
                        );
                      }
                    });

                    const allQuestions: Array<{ _id: string; type?: string; question?: string; questionImg?: string; }> =
                      (questionsData?.questions as any[]) || [];

                    // Compute type options from fetched questions as string[]
                    const allTypes: string[] = Array.from(
                      new Set<string>(
                        allQuestions.map((q) =>
                          String(q.type || "UNKNOWN").toUpperCase()
                        )
                      )
                    ).sort();

                    const typeOptions: string[] = ["", ...allTypes]; // "" => All Types

                    // Apply per-section type filter safely as string
                    const currentFilter = String(section.typeFilter || "").toUpperCase();

                    let availableQuestions = allQuestions.filter(
                      (q) => !otherSelectedIds.has(q._id)
                    );
                    if (currentFilter) {
                      availableQuestions = availableQuestions.filter(
                        (q) => String(q.type || "UNKNOWN").toUpperCase() === currentFilter
                      );
                    }

                    const sectionMarks = section.questions.reduce(
                      (acc: number, q: any) => acc + Number(q.marks || 0),
                      0
                    );
                    const intendedMax = computeSectionIntendedMax(section);

                    return (
                      <Box
                        key={sIdx}
                        p={4}
                        borderWidth="1px"
                        borderRadius="md"
                        mb={4}
                        bg="gray.50"
                      >
                        <Flex justify="space-between" align="center" mb={2} gap={3} wrap="wrap">
                          <Text fontWeight="bold">
                            {`Section ${sIdx + 1}: ${section.name || "(Unnamed)"}`}
                          </Text>
                          <HStack spacing={2}>
                            <Badge colorScheme="purple" variant="subtle">
                              Section marks (selected): {sectionMarks}
                            </Badge>
                            <Badge colorScheme="blue" variant="subtle">
                              Max possible (guide): {intendedMax}
                            </Badge>
                            <IconButton
                              aria-label="Remove section"
                              icon={<DeleteIcon />}
                              size="sm"
                              colorScheme="red"
                              variant="outline"
                              onClick={() => removeSection(sIdx)}
                            />
                          </HStack>
                        </Flex>

                        <Stack spacing={3}>
                          <Box>
                            <FormLabel>Section Name</FormLabel>
                            <Input
                              placeholder="e.g., Explain in detail"
                              value={section.name}
                              onChange={(e) =>
                                setEditedPaper((prev: any) => {
                                  const next = { ...prev };
                                  next.sections[sIdx].name = e.target.value;
                                  return next;
                                })
                              }
                            />
                          </Box>

                          {/* Answer rule and related settings */}
                          <Flex gap={3} wrap="wrap">
                            <Box minW="260px" flex="1">
                              <FormLabel>Answer rule</FormLabel>
                              <Select
                                value={section.selectionType || "ALL"}
                                onChange={(e) =>
                                  setEditedPaper((prev: any) => {
                                    const next = { ...prev };
                                    next.sections[sIdx].selectionType = e.target.value;
                                    if (e.target.value === "ALL") {
                                      next.sections[sIdx].optionalCount = undefined;
                                    }
                                    return next;
                                  })
                                }
                              >
                                <option value="ALL">All questions</option>
                                <option value="ANY">Exactly N questions</option>
                                <option value="AT_LEAST">At least N questions</option>
                                <option value="AT_MOST">At most N questions</option>
                              </Select>
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                Choose how many must be answered.
                              </Text>
                            </Box>

                            {section.selectionType && section.selectionType !== "ALL" && (
                              <Box minW="140px" flex="1">
                                <FormLabel>Number (N)</FormLabel>
                                <NumberInput
                                  min={1}
                                  max={section.questions.length || 1}
                                  value={section.optionalCount || 1}
                                  onChange={(_s, val) =>
                                    setEditedPaper((prev: any) => {
                                      const next = { ...prev };
                                      next.sections[sIdx].optionalCount = val || 1;
                                      return next;
                                    })
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                  N cannot exceed questions in this section.
                                </Text>
                              </Box>
                            )}

                            <Box minW="260px" flex="1">
                              <FormLabel>If more than allowed are answered</FormLabel>
                              <Select
                                value={section.selectionPolicy || "FIRST_N"}
                                onChange={(e) =>
                                  setEditedPaper((prev: any) => {
                                    const next = { ...prev };
                                    next.sections[sIdx].selectionPolicy = e.target.value;
                                    return next;
                                  })
                                }
                              >
                                <option value="FIRST_N">Score first N answers</option>
                                <option value="BEST_N">Score best N answers</option>
                              </Select>
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                How to pick which answers to score.
                              </Text>
                            </Box>

                            <Box minW="260px" flex="1">
                              <FormLabel>How section marks are calculated</FormLabel>
                              <Select
                                value={section.sectionMarksMode || "SUM"}
                                onChange={(e) =>
                                  setEditedPaper((prev: any) => {
                                    const next = { ...prev };
                                    next.sections[sIdx].sectionMarksMode = e.target.value;
                                    if (e.target.value !== "CAP") {
                                      next.sections[sIdx].sectionMarksCap = undefined;
                                    }
                                    return next;
                                  })
                                }
                              >
                                <option value="SUM">Add marks of counted answers</option>
                                <option value="CAP">Cap at a maximum</option>
                              </Select>
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                SUM adds up counted answers; CAP limits to a maximum.
                              </Text>
                            </Box>

                            {section.sectionMarksMode === "CAP" && (
                              <Box minW="160px" flex="1">
                                <FormLabel>Cap marks</FormLabel>
                                <NumberInput
                                  min={1}
                                  value={section.sectionMarksCap || 0}
                                  onChange={(_s, val) =>
                                    setEditedPaper((prev: any) => {
                                      const next = { ...prev };
                                      next.sections[sIdx].sectionMarksCap = val || 0;
                                      return next;
                                    })
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </Box>
                            )}

                            <Box minW="180px" display="flex" alignItems="flex-end">
                              <Checkbox
                                isChecked={!!section.shuffleQuestions}
                                onChange={(e) =>
                                  setEditedPaper((prev: any) => {
                                    const next = { ...prev };
                                    next.sections[sIdx].shuffleQuestions = e.target.checked;
                                    return next;
                                  })
                                }
                              >
                                Shuffle questions
                              </Checkbox>
                            </Box>
                          </Flex>

                          <Box>
                            <FormLabel>Student instructions (optional)</FormLabel>
                            <Input
                              placeholder="e.g., Answer any 4 out of the following 5 questions."
                              value={section.instructions || ""}
                              onChange={(e) =>
                                setEditedPaper((prev: any) => {
                                  const next = { ...prev };
                                  next.sections[sIdx].instructions = e.target.value;
                                  return next;
                                })
                              }
                            />
                          </Box>

                          {/* Question Type Filter per section (fixed types) */}
                          <Box>
                            <FormLabel>Question Type Filter</FormLabel>
                            <Select
                              placeholder="All Types"
                              value={String(section.typeFilter || "")}
                              onChange={(e) =>
                                setEditedPaper((prev: any) => {
                                  const next = { ...prev };
                                  next.sections[sIdx].typeFilter = e.target.value;
                                  return next;
                                })
                              }
                              maxW="280px"
                            >
                              {typeOptions.map((t: string) => (
                                <option key={(t || "ALL") as string} value={String(t)}>
                                  {t ? t : "All Types"}
                                </option>
                              ))}
                            </Select>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              Filter the available list below by question type.
                            </Text>
                          </Box>

                          {/* Questions table */}
                          <Box>
                            <FormLabel>Questions</FormLabel>
                            {questionsLoading ? (
                              <Flex justify="center">
                                <Spinner />
                              </Flex>
                            ) : (
                              <Table variant="simple" size="sm" mt={2}>
                                <Thead>
                                  <Tr>
                                    <Th>Select</Th>
                                    <Th>Image</Th>
                                    <Th>Question</Th>
                                    <Th>Type</Th>
                                    <Th>Marks</Th>
                                    <Th>Requires Upload</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {(!availableQuestions ||
                                    availableQuestions.length === 0) && (
                                    <Tr>
                                      <Td colSpan={6} textAlign="center">
                                        {questionsLoading
                                          ? "Loading questions..."
                                          : "No questions available for selected subject/type"}
                                      </Td>
                                    </Tr>
                                  )}
                                  {availableQuestions?.map((q: any) => {
                                    const isSelected = section.questions.some(
                                      (qs: any) => qs.questionId === q._id
                                    );
                                    const qEntry = section.questions.find(
                                      (qs: any) => qs.questionId === q._id
                                    );
                                    const qMarks = qEntry?.marks || 1;
                                    const requiresUpload = !!qEntry?.requiresStudentUpload;

                                    return (
                                      <Tr key={q._id}>
                                        <Td>
                                          <Checkbox
                                            isChecked={isSelected}
                                            disabled={!isSelected && selectedQuestionIds.has(q._id)}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                const nextList = [
                                                  ...section.questions,
                                                  {
                                                    questionId: q._id,
                                                    marks: 1,
                                                    requiresStudentUpload: false,
                                                    ...q, // snapshot for view mode
                                                  },
                                                ];
                                                updateSectionQuestions(sIdx, nextList);
                                              } else {
                                                updateSectionQuestions(
                                                  sIdx,
                                                  section.questions.filter(
                                                    (qq: any) => qq.questionId !== q._id
                                                  )
                                                );
                                              }
                                            }}
                                          />
                                        </Td>
                                        <Td>
                                          <QuestionThumb question={q} />
                                        </Td>
                                        <Td
                                          maxW="300px"
                                          whiteSpace="normal"
                                          wordBreak="break-word"
                                        >
                                          {q.question}
                                        </Td>
                                        <Td>{String(q.type || "-").toUpperCase()}</Td>
                                        <Td>
                                          {isSelected && (
                                            <NumberInput
                                              min={1}
                                              max={editedPaper.totalMarks}
                                              value={qMarks}
                                              size="sm"
                                              width="80px"
                                              onChange={(_str, val) => {
                                                const updated = section.questions.map(
                                                  (qs: any) =>
                                                    qs.questionId === q._id
                                                      ? { ...qs, marks: val || 1 }
                                                      : qs
                                                );
                                                updateSectionQuestions(sIdx, updated);
                                              }}
                                            >
                                              <NumberInputField />
                                            </NumberInput>
                                          )}
                                        </Td>
                                        <Td>
                                          {isSelected && (
                                            <Checkbox
                                              isChecked={requiresUpload}
                                              onChange={(e) => {
                                                const updated = section.questions.map((qs: any) =>
                                                  qs.questionId === q._id
                                                    ? {
                                                        ...qs,
                                                        requiresStudentUpload: e.target.checked,
                                                      }
                                                    : qs
                                                );
                                                updateSectionQuestions(sIdx, updated);
                                              }}
                                            >
                                              Requires Upload
                                            </Checkbox>
                                          )}
                                        </Td>
                                      </Tr>
                                    );
                                  })}
                                </Tbody>
                              </Table>
                            )}
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <VStack align="start" spacing={4}>
                  <Text>
                    <b>Title:</b> {editedPaper.title}
                  </Text>
                  <Text>
                    <b>Subject:</b> {editedPaper.subject}
                  </Text>
                  <Text>
                    <b>Program:</b>{" "}
                    {programs.find((p: any) => p._id === editedPaper.programId)?.name ||
                      editedPaper.programId}
                  </Text>
                  <Text>
                    <b>Duration:</b> {editedPaper.duration} mins
                  </Text>
                  <Text>
                    <b>Total Marks:</b> {editedPaper.totalMarks}
                  </Text>
                  <Divider />
                  {editedPaper.sections.map((section: any, idx: number) => (
                    <Box
                      key={idx}
                      p={3}
                      borderWidth="1px"
                      borderRadius="md"
                      w="100%"
                      bg="gray.50"
                      mb={4}
                    >
                      <Text fontWeight="bold" mb={2}>
                        {section.name}
                      </Text>
                      {section.instructions && (
                        <Text fontSize="sm" color="gray.600" mb={2}>
                          Instructions: {section.instructions}
                        </Text>
                      )}
                      {section.questions.length === 0 && (
                        <Text>No questions in this section.</Text>
                      )}
                      {section.questions.map((sq: any, qIdx: number) => {
                        const q = sq; // snapshot
                        return (
                          <HStack key={qIdx} align="start" spacing={3} mb={2}>
                            <Box minW="120px">
                              <QuestionPreviewImage question={q} />
                            </Box>
                            <Box>
                              <Text>
                                {q.question || `Question ID: ${sq.questionId}`} - Marks:{" "}
                                {sq.marks}
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                Type: {String(q.type || "-").toUpperCase()}{" "}
                                {sq.requiresStudentUpload ? "(Requires Upload)" : ""}
                              </Text>
                            </Box>
                          </HStack>
                        );
                      })}
                    </Box>
                  ))}
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              {editMode ? (
                <>
                  <Button
                    colorScheme="green"
                    onClick={handleUpdate}
                    isLoading={updateMutation.isLoading}
                    mr={3}
                  >
                    Save Changes
                  </Button>
                  <Button variant="ghost" onClick={() => setEditMode(false)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => {
                    setEditMode(true);
                    if (subjectForQuery) refetchQuestions();
                  }}
                  colorScheme="blue"
                >
                  Edit
                </Button>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );

  function updateSectionQuestions(sectionIndex: number, questions: any[]) {
    const newSections = [...editedPaper.sections];
    newSections[sectionIndex] = { ...newSections[sectionIndex], questions };
    setEditedPaper({ ...editedPaper, sections: newSections });
  }
};

export default AllQuestionPapers;
